"use client";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { brandPages } from "@/content/enterprise/brand-pages";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";
import { brandHref, enterpriseBrands } from "@/content/enterprise/brands";

// Shared shell for enterprise brand pages. The SUBSTANCE lives in
// content/enterprise/brand-pages.ts — a brand only gets a page when it has
// genuinely brand-specific supply items and FAQs (anti-doorway rule).
const buyingSteps = [
  { step: "01", title: "Enquiry", detail: "Requirements, quantity and timeline." },
  { step: "02", title: "Quotation in RM", detail: "Local pricing, usually within two working days." },
  { step: "03", title: "Purchase order", detail: "Standard PO terms; MyInvois e-invoice issued." },
  { step: "04", title: "Authorised supply", detail: "Sourced through authorised Malaysian distribution." },
  { step: "05", title: "Delivery & licensing", detail: "Licence issuance or hardware delivery, tracked." },
  { step: "06", title: "Renewals handled", detail: "Co-termed and diarised — we chase the dates, not you." },
];

export default function BrandPage({ slug }: { slug: string }) {
  const locale = useLocale();
  const reduce = useReducedMotion();
  const page = brandPages[slug];
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        animate: { y: 0 },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);
  if (!page) return null;
  const nameOf = (s: string) => enterpriseBrands.find((b) => b.slug === s)?.name ?? s;
  // A brand with pre-purchase conditions owns its own intake form (NVIDIA's
  // export KYC). Everyone else keeps the shared, brand-prefilled enquiry.
  const enquiryHref = page.compliance
    ? page.compliance.cta.href
    : `/enterprise/enquiry/?brand=${page.slug}`;
  const waMessage = `Hi Anchor Sprint, I'd like a quotation for ${page.brand} (from your ${page.brand} page).`;
  const waLocation = `brand_${page.slug.replace(/-malaysia$/, "").replace(/-/g, "_")}`;

  return (
    <main className="bg-background">
      <section className="pb-14 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...heroIn} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Enterprise Solutions")} · {t(locale, page.categoryLabel)}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-6xl">
              {t(locale, page.h1)}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted md:text-xl">
              {t(locale, page.intro)}
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
              <Link
                href={lp(enquiryHref)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Request a quotation")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <WhatsAppCta
                message={waMessage}
                location={`${waLocation}_hero`}
                label={t(locale, "WhatsApp us")}
                className="px-8"
              />
              <a
                href="#how-buying-works"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
              >
                {t(locale, "How buying works")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "What we supply")}
          </motion.h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {page.supply.map((s) => (
              <motion.div {...inView} key={s.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, s.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, s.detail)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-purchase conditions — sits directly after supply, because it changes
          what buying the item above actually involves. Optional: only NVIDIA
          carries one today. */}
      {page.compliance && (
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div {...inView} className="rounded-lg border border-warning/40 bg-warning/5 p-8">
              <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
                {t(locale, page.compliance.heading)}
              </h2>
              <p className="mt-4 leading-relaxed text-neutral-muted">
                {t(locale, page.compliance.body)}
              </p>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-muted">
                {t(locale, "What we ask for")}
              </p>
              <ul className="mt-3 space-y-2">
                {page.compliance.points.map((p) => (
                  <li key={p} className="flex gap-3 text-sm leading-relaxed text-neutral-muted">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-warning" aria-hidden />
                    {t(locale, p)}
                  </li>
                ))}
              </ul>
              <Link
                href={lp(page.compliance.cta.href)}
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, page.compliance.cta.label)}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      <section id="how-buying-works" className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView}>
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "How buying works in Malaysia")}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {t(
                locale,
                "Quotation-driven, in Ringgit, with the paperwork handled — the parts of enterprise procurement most catalogue listings leave you to discover.",
              )}
            </p>
          </motion.div>
          <motion.ol {...inView} className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {buyingSteps.map((s) => (
              <li key={s.step} className="bg-card p-6">
                <span className="font-display text-sm font-bold text-primary">{s.step}</span>
                <h3 className="mt-2 font-semibold text-neutral-emphasis">{t(locale, s.title)}</h3>
                <p className="mt-1 text-sm text-neutral-muted">{t(locale, s.detail)}</p>
              </li>
            ))}
          </motion.ol>
          <motion.p {...inView} className="mt-8 text-sm text-neutral-muted">
            {t(locale, page.trademark)}
          </motion.p>
        </div>
      </section>

      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, page.bridge.heading)}
          </motion.h2>
          <motion.p {...inView} className="mx-auto mt-4 max-w-2xl text-neutral-muted">
            {t(locale, page.bridge.body)}
          </motion.p>
          <motion.div {...inView} className="mt-8">
            <Link href={lp(page.bridge.href)} className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              {t(locale, page.bridge.cta)}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <StructuredData type="faq" faqs={page.faqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, page.brand)} {t(locale, "in Malaysia — common questions")}
          </motion.h2>
          <div className="mt-8 space-y-6">
            {page.faqs.map((f) => (
              <motion.div {...inView} key={f.question} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis">
            {t(locale, "Get a quotation in RM")}
          </motion.h2>
          <motion.div
            {...inView}
            className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link
              href={lp(enquiryHref)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "Request quotation")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <WhatsAppCta
              message={waMessage}
              location={`${waLocation}_closing_cta`}
              label={t(locale, "WhatsApp us")}
              className="px-8"
            />
          </motion.div>
          <motion.p {...inView} className="mt-8 text-sm text-neutral-muted">
            {t(locale, "Related in the directory:")}{" "}
            {/* A related brand only has a page when it is published. Linking to
                /enterprise/{slug}/ unconditionally produced 13 internal 404s
                across the eight brand pages — every unpublished sibling. Those
                brands are still quotable, so the link goes to the enquiry form
                pre-filled with the brand rather than being dropped. */}
            {page.related.map((r, i) => {
              const brand = enterpriseBrands.find((b) => b.slug === r);
              const href = lp(brand ? brandHref(brand) : `/enterprise/enquiry/?brand=${r}`);
              return (
                <span key={r}>
                  {i > 0 && " · "}
                  <Link href={href} className="text-primary hover:underline">
                    {nameOf(r)}
                  </Link>
                </span>
              );
            })}
            {" · "}
            <Link href={lp("/enterprise/")} className="text-primary hover:underline">
              {t(locale, "all brands")}
            </Link>
          </motion.p>
        </div>
      </section>
    </main>
  );
}
